import { useDeferredValue, useMemo } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { Layout } from 'react-grid-layout'
import { useTranslation } from 'react-i18next'

import { LightModeSemanticColors } from '@enechain/ecloud-designsystem'

import { CardGridLayout, GridCard } from '~/components/common/CardGridLayout'
import { BgChart } from '~/pages/BalanceGeneration/components/BgChart'
import type { GenSeriesVisibility } from '~/pages/BalanceGeneration/constants'
import type { BgApiData } from '~/pages/BalanceGeneration/hooks/useGenerationDashboard'

type Props = {
  visibleAreaCodes: Set<AreaCode>
  seriesVisibility: GenSeriesVisibility
  bgs: BgApiData[]
  gcKoma: number
  /** 表示中の日付 (BgChart の GC 縦線を当日だけ描くために通す)。 */
  displayDate: Date
  /** workflow-back-bar-readonly AC-14: 詳細リンクへ引き継ぐ caseId (undefined = 引き継がない)。 */
  caseId?: string
  /** `useGridCardLayout` (呼び出し元 = `BalanceGenerationPage`) が計算したカード配置。 */
  layout: Layout
  /** ドラッグ / リサイズ確定時にそのまま渡すコールバック (`useGridCardLayout.onLayoutSettled`)。 */
  onLayoutSettled: (layout: Layout) => void
}

/**
 * エリア絞り込み後の表示対象 BG 一覧 (doji-hatsu.md AC-BDH-005: チェックされて
 * いる全エリアに属する BG カードを表示する)。`BalanceGenerationPage` (cardIds
 * 算出) と `BgGrid` (実描画) の双方がこの 1 関数を通すことで、絞り込みロジックが
 * 2 箇所で独立に古くなることを防ぐ。
 *
 * `AreaCode.UNSPECIFIED` の BG (backend `GetGenerationDashboard` が
 * bg_master 未解決・BG コード形式不一致の発電BGに付ける値。
 * `go/internal/backend/application/usecase/balance/generation.go` の godoc
 * 「the row is still included ... (not dropped)」のとおり意図的に残す) は、
 * エリア選択状態と無関係に常に表示する (2026-09-09 ユーザー裁定)。
 * 選択集合との積を取らない理由: エリア不明 BG を業務エリアフィルタとどう
 * 関係づけるかは spec 未定義であり、「業務エリア外だから隠す」という新しい
 * observable behavior を frontend 側で独自に持ち込まないため
 * (PdM 確認 issue は別途起票)。旧実装 (radio の既定 `undefined`=無フィルタ) は
 * これらの行を落とさずに表示していたため、この分岐が無いと checkbox 化で
 * リグレッションする。
 */
export function selectVisibleBgs(
  bgs: BgApiData[],
  visibleAreaCodes: Set<AreaCode>,
): BgApiData[] {
  return bgs.filter(
    (bg) =>
      bg.config.areaCode === AreaCode.UNSPECIFIED ||
      visibleAreaCodes.has(bg.config.areaCode),
  )
}

export const BgGrid = ({
  visibleAreaCodes,
  seriesVisibility,
  bgs,
  gcKoma,
  displayDate,
  caseId,
  layout,
  onLayoutSettled,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const deferredSeriesVisibility = useDeferredValue(seriesVisibility)

  const visibleBgs = useMemo(
    () => selectVisibleBgs(bgs, visibleAreaCodes),
    [visibleAreaCodes, bgs],
  )

  if (visibleBgs.length === 0) {
    return (
      <div
        style={{
          padding: '40px 20px',
          textAlign: 'center',
          color: LightModeSemanticColors.Text.midEmphasis.value,
        }}
      >
        {t('page:balanceGeneration.empty')}
      </div>
    )
  }

  return (
    <CardGridLayout layout={layout} onLayoutSettled={onLayoutSettled}>
      {visibleBgs.map((bg) => (
        <GridCard key={bg.config.code}>
          <BgChart
            bgConfig={bg.config}
            caseId={caseId}
            displayDate={displayDate}
            gcKoma={gcKoma}
            komaData={bg.komaData}
            seriesVisibility={deferredSeriesVisibility}
            summary={bg.summary}
          />
        </GridCard>
      ))}
    </CardGridLayout>
  )
}
